import { DataGrid, type GridColumn } from "@/components/data-grid";
import { EmptyState } from "@/components/EmptyState";
import { OverviewIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";

import { RECENT_BATCHES_PREVIEW } from "../lib/limits.ts";
import { errorMessage } from "../lib/query.ts";
import { analytics } from "../lib/telemetry/analytics.ts";

import { useWorkspace } from "../app/AppShell.tsx";
import { useDashboardPeriod } from "../app/DashboardShell.tsx";
import { mechanicsQuery } from "../mechanics/mechanics.ts";
import { deriveAlerts } from "./alerts.ts";
import { AlertsBand } from "./AlertsBand.tsx";
import {
  batchesQuery,
  batchStatusLabel,
  coverageQuery,
  points,
  type CoverageKpi,
} from "./batches.ts";
import { isWholeYear } from "./periods.ts";

/** Fuera del componente: dentro cambiarían de identidad en cada render. */
const COVERAGE_COLUMNS: readonly GridColumn<CoverageKpi>[] = [
  { key: "kpiName", label: "KPI", render: (k) => k.kpiName },
  {
    key: "participants",
    label: "Personas",
    numeric: true,
    render: (k) => k.participants,
  },
  {
    key: "points",
    label: "Puntos",
    numeric: true,
    render: (k) => points(k.points),
    sortValue: (k) => k.points,
  },
  { key: "batches", label: "Lotes", numeric: true, render: (k) => k.batches },
];

/**
 * El Resumen: el panorama del mes.
 *
 * Las alertas (§7.5) viven aquí y no en una sección aparte, junto a la
 * cobertura y los lotes recientes, porque las tres responden a la misma
 * pregunta --¿qué pasa con este mes?-- y separarlas obligaría a mirar en tres
 * sitios para contestarla.
 *
 * Las tres consultas son del catálogo y se piden a la vez; la banda se dibuja
 * con lo que haya llegado (§ `deriveAlerts`).
 *
 * Es MENSUAL, y por eso con un año entero elegido arriba no dibuja: cobertura,
 * lotes y mecánicas se piden mes a mes, y un año no es un periodo que el
 * catálogo entienda. Se dice y se pide un mes, que es mejor que enseñar tres
 * errores del servidor y dejar al usuario adivinando cuál es el problema.
 */
export function OverviewPanel() {
  const { period } = useDashboardPeriod();
  const { askInNewConversation } = useWorkspace();
  const [allKpis, setAllKpis] = useState(false);
  const [allBatches, setAllBatches] = useState(false);

  // Un año entero no se pregunta: ni se pide ni se reintenta al volver.
  const monthly = !isWholeYear(period);
  const coverage = useQuery({ ...coverageQuery(period), enabled: monthly });
  const batches = useQuery({ ...batchesQuery(period), enabled: monthly });
  const mechanics = useQuery({ ...mechanicsQuery(period), enabled: monthly });

  const alerts = useMemo(
    () =>
      deriveAlerts({
        period,
        coverage: coverage.data,
        batches: batches.data?.batches,
        mechanics: mechanics.data?.mechanics,
      }),
    [period, coverage.data, batches.data, mechanics.data],
  );

  const error = coverage.error ?? batches.error ?? mechanics.error;
  const rows = batches.data?.batches ?? [];
  /* Los descartados no cuentan como lotes del mes: no aportaron un solo
     resultado, y contarlos junto a la cobertura haría pensar que sí. */
  const counted = rows.filter((batch) => batch.status !== "DISCARDED").length;
  const shown = allBatches ? rows : rows.slice(0, RECENT_BATCHES_PREVIEW);

  if (!monthly) {
    return (
      <section className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
        <EmptyState icon={OverviewIcon}>
          El resumen es de un mes: la cobertura, los lotes y la mecánica lo son.
          Elige uno ahí arriba, donde ahora pone{" "}
          <span className="font-mono">{period}</span>.
        </EmptyState>
      </section>
    );
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
      {error != null && (
        <Alert variant="destructive">
          <AlertTitle>No se pudo cargar el mes</AlertTitle>
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      )}

      <AlertsBand alerts={alerts} onAsk={askInNewConversation} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Cobertura del mes">
          {coverage.data === undefined ? (
            <p className="text-sm text-muted-foreground">
              {coverage.isPending ? "Cargando…" : "Sin datos de cobertura."}
            </p>
          ) : (
            <>
              <p className="text-2xl font-semibold">
                {coverage.data.withResults.toLocaleString("es")}
                <span className="text-muted-foreground">
                  /{coverage.data.enrolled.toLocaleString("es")}
                </span>{" "}
                <span className="text-base font-normal text-muted-foreground">
                  con datos
                </span>
              </p>
              <p className="text-sm text-muted-foreground">
                {coverage.data.kpis.length}{" "}
                {coverage.data.kpis.length === 1 ? "KPI" : "KPIs"} · {counted}{" "}
                {counted === 1 ? "lote" : "lotes"}
              </p>

              {coverage.data.kpis.length > 0 &&
                (allKpis ? (
                  <DataGrid
                    className="mt-3"
                    columns={COVERAGE_COLUMNS}
                    rows={coverage.data.kpis}
                  />
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-fit"
                    onClick={() => {
                      analytics.work.overviewExpanded({
                        section: "overview",
                        block: "kpis",
                      });
                      setAllKpis(true);
                    }}
                  >
                    Ver el detalle
                  </Button>
                ))}
            </>
          )}
        </Panel>

        <Panel title="Lotes recientes">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {batches.isPending
                ? "Cargando…"
                : "Ningún lote en este mes todavía."}
            </p>
          ) : (
            <>
              <ul className="grid gap-px overflow-hidden rounded-md border bg-border">
                {shown.map((batch) => (
                  <li key={batch.id}>
                    <Link
                      to="/work/lotes/$batchId"
                      params={{ batchId: batch.id }}
                      search={(previous) => previous}
                      onClick={() => {
                        analytics.work.batchOpened({
                          section: "overview",
                          origin: "recent",
                        });
                      }}
                      className="flex items-baseline justify-between gap-3 bg-card px-3 py-2 transition-colors hover:bg-secondary"
                    >
                      <span className="truncate text-sm font-medium">
                        {batch.label}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {batchStatusLabel(batch.status)} ·{" "}
                        {points(batch.totalPoints)} pts
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              {!allBatches && rows.length > shown.length && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-fit"
                  onClick={() => {
                    analytics.work.overviewExpanded({
                      section: "overview",
                      block: "batches",
                    });
                    setAllBatches(true);
                  }}
                >
                  Ver todos ({rows.length})
                </Button>
              )}
            </>
          )}
        </Panel>
      </div>
    </section>
  );
}

/** El marco común de los paneles del Resumen: título arriba y su contenido. */
function Panel({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-lg border bg-card p-4">
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
      </h2>
      {children}
    </section>
  );
}
